import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit3, Search, AlertTriangle, X, Server, Gift
} from 'lucide-react';

const SKU_OPTIONS = [
  { value: 'TRIAL',      label: 'Trial (14 days)', maxEndpoints: 9999, maxUsers: 10,  maxIntegrations: 5  },
  { value: 'PRO',        label: 'Pro',             maxEndpoints: 9999, maxUsers: 25,  maxIntegrations: 10 },
  { value: 'ENTERPRISE', label: 'Enterprise',      maxEndpoints: 9999, maxUsers: 999, maxIntegrations: 99 },
];

const STATUS_COLORS = {
  ACTIVE:    { bg: 'rgba(34, 197, 94, 0.15)',  color: '#22c55e' },
  TRIAL:     { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
  EXPIRED:   { bg: 'rgba(239, 68, 68, 0.15)',  color: '#ef4444' },
  SUSPENDED: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
  REVOKED:   { bg: 'rgba(156, 163, 175, 0.15)', color: '#9ca3af' },
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const CcmLicensesTab = ({ tenants }) => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    tenantId: '',
    sku: 'PRO',
    maxEndpoints: 9999,
    maxUsers: 25,
    maxIntegrations: 10,
    status: 'ACTIVE',
    notes: '',
  });

  const fetchLicenses = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/ccm/licenses', { credentials: 'include' });
      if (res.ok) {
        setLicenses(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch CCM licenses:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLicenses(); }, [fetchLicenses]);

  const licensedTenantIds = licenses.map(l => l.tenantId);
  const unlicensedTenants = (tenants || []).filter(t => !licensedTenantIds.includes(t.id));

  const resetForm = () => {
    setFormData({
      tenantId: '',
      sku: 'PRO',
      maxEndpoints: 9999,
      maxUsers: 25,
      maxIntegrations: 10,
      status: 'ACTIVE',
      notes: '',
    });
  };

  const handleSkuChange = (sku) => {
    const def = SKU_OPTIONS.find(s => s.value === sku);
    setFormData(prev => ({
      ...prev,
      sku,
      maxEndpoints: def?.maxEndpoints ?? prev.maxEndpoints,
      maxUsers: def?.maxUsers ?? prev.maxUsers,
      maxIntegrations: def?.maxIntegrations ?? prev.maxIntegrations,
    }));
  };

  const handleCreate = async () => {
    if (!formData.tenantId) return;
    try {
      const res = await fetch('/api/platform/ccm/licenses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setShowCreateModal(false);
        resetForm();
        fetchLicenses();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create license');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleStartTrial = async (tenantId) => {
    try {
      const res = await fetch(`/api/platform/ccm/licenses/${tenantId}/trial`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        fetchLicenses();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to start trial');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdate = async () => {
    if (!editingLicense) return;
    try {
      const res = await fetch(`/api/platform/ccm/licenses/${editingLicense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sku: formData.sku,
          status: formData.status,
          maxEndpoints: formData.maxEndpoints,
          maxUsers: formData.maxUsers,
          maxIntegrations: formData.maxIntegrations,
          notes: formData.notes,
        }),
      });
      if (res.ok) {
        setEditingLicense(null);
        resetForm();
        fetchLicenses();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to update license');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRevoke = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`/api/platform/ccm/licenses/${deleteTarget.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setDeleteTarget(null);
        fetchLicenses();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openEdit = (license) => {
    setFormData({
      tenantId: license.tenantId,
      sku: license.sku,
      maxEndpoints: license.maxEndpoints,
      maxUsers: license.maxUsers,
      maxIntegrations: license.maxIntegrations,
      status: license.status,
      notes: license.notes || '',
    });
    setEditingLicense(license);
  };

  const filtered = licenses.filter(l => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return l.tenant?.name?.toLowerCase().includes(s) ||
      l.sku?.toLowerCase().includes(s) ||
      l.status?.toLowerCase().includes(s);
  });

  if (loading) {
    return <div className="platform-loading"><div className="platform-spinner" /></div>;
  }

  return (
    <div className="md-licenses-tab">
      <div className="md-lic-stats">
        <div className="md-lic-stat">
          <span className="md-lic-stat-value">{licenses.length}</span>
          <span className="md-lic-stat-label">Total Licenses</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#22c55e' }}>
            {licenses.filter(l => l.status === 'ACTIVE' && l.sku !== 'TRIAL').length}
          </span>
          <span className="md-lic-stat-label">Active Paid</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#f59e0b' }}>
            {licenses.filter(l => l.sku === 'TRIAL' && !l.isExpired).length}
          </span>
          <span className="md-lic-stat-label">Trial</span>
        </div>
        <div className="md-lic-stat">
          <span className="md-lic-stat-value" style={{ color: '#ef4444' }}>
            {licenses.filter(l => l.isExpired || l.status === 'EXPIRED').length}
          </span>
          <span className="md-lic-stat-label">Expired</span>
        </div>
      </div>

      <div className="md-lic-toolbar">
        <div className="md-lic-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
          <Plus size={16} /> Assign License
        </button>
      </div>

      {unlicensedTenants.length > 0 && (
        <div className="md-lic-unlicensed">
          <div className="md-lic-unlicensed-header">
            <Gift size={16} />
            <span>{unlicensedTenants.length} tenant{unlicensedTenants.length !== 1 ? 's' : ''} without a CCM license</span>
          </div>
          <div className="md-lic-unlicensed-list">
            {unlicensedTenants.map(t => (
              <div key={t.id} className="md-lic-unlicensed-item">
                <span>{t.name}</span>
                <button className="btn btn-secondary btn-sm" onClick={() => handleStartTrial(t.id)}>
                  Start 14-day Trial
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="platform-empty">
          <div className="platform-empty-icon"><Server size={32} /></div>
          <h3>No CCM Licenses</h3>
          <p>Assign ServerliCCM licenses to tenants to enable feature access.</p>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreateModal(true); }}>
            <Plus size={16} /> Assign License
          </button>
        </div>
      ) : (
        <div className="md-lic-table-wrap">
          <table className="md-lic-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Endpoints</th>
                <th>Users</th>
                <th>Integrations</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lic => {
                const effectiveStatus = lic.isExpired ? 'EXPIRED' : (lic.sku === 'TRIAL' && lic.status === 'ACTIVE' ? 'TRIAL' : lic.status);
                const statusStyle = STATUS_COLORS[effectiveStatus] || STATUS_COLORS.REVOKED;
                const endpointPercent = lic.maxEndpoints > 0
                  ? Math.round((lic.usage?.currentEndpoints || 0) / lic.maxEndpoints * 100)
                  : 0;
                const isOverLimit = (lic.usage?.currentEndpoints || 0) > lic.maxEndpoints;

                return (
                  <tr key={lic.id}>
                    <td>
                      <div className="md-lic-tenant">
                        <span className="md-lic-tenant-name">{lic.tenant?.name || 'Unknown'}</span>
                        <span className="md-lic-tenant-id">{lic.tenantId?.slice(0, 8)}...</span>
                      </div>
                    </td>
                    <td>
                      <span className="md-lic-sku">{lic.sku}</span>
                    </td>
                    <td>
                      <span
                        className="md-lic-status"
                        style={{ background: statusStyle.bg, color: statusStyle.color }}
                      >
                        {effectiveStatus}
                        {lic.sku === 'TRIAL' && lic.daysRemaining !== null && !lic.isExpired && (
                          <span className="md-lic-trial-days">{lic.daysRemaining}d left</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="md-lic-usage">
                        <span className={isOverLimit ? 'md-lic-over' : ''}>
                          {lic.usage?.currentEndpoints || 0} / {lic.maxEndpoints >= 9999 ? '\u221E' : lic.maxEndpoints}
                        </span>
                        <div className="md-lic-bar">
                          <div
                            className={`md-lic-bar-fill ${isOverLimit ? 'over' : endpointPercent > 80 ? 'warn' : ''}`}
                            style={{ width: `${Math.min(100, endpointPercent)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td>
                      {lic.usage?.currentUsers || 0} / {lic.maxUsers >= 999 ? '\u221E' : lic.maxUsers}
                    </td>
                    <td>
                      {lic.usage?.currentIntegrations || 0} / {lic.maxIntegrations >= 99 ? '\u221E' : lic.maxIntegrations}
                    </td>
                    <td>
                      {lic.sku === 'TRIAL' && lic.trialEndsAt
                        ? formatDate(lic.trialEndsAt)
                        : lic.expiresAt
                          ? formatDate(lic.expiresAt)
                          : 'Never'}
                    </td>
                    <td>
                      <div className="md-lic-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(lic)} title="Edit">
                          <Edit3 size={14} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(lic)} title="Revoke">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="platform-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><Plus size={18} /> Assign CCM License</h3>
              <button onClick={() => setShowCreateModal(false)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="form-group">
                <label>Tenant</label>
                <select
                  className="form-select"
                  value={formData.tenantId}
                  onChange={e => setFormData(prev => ({ ...prev, tenantId: e.target.value }))}
                >
                  <option value="">Select tenant...</option>
                  {unlicensedTenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Plan</label>
                <select
                  className="form-select"
                  value={formData.sku}
                  onChange={e => handleSkuChange(e.target.value)}
                >
                  {SKU_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="md-lic-form-row">
                <div className="form-group">
                  <label>Max Endpoints</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxEndpoints}
                    onChange={e => setFormData(prev => ({ ...prev, maxEndpoints: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Users</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxUsers}
                    onChange={e => setFormData(prev => ({ ...prev, maxUsers: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Integrations</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxIntegrations}
                    onChange={e => setFormData(prev => ({ ...prev, maxIntegrations: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-input"
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Internal notes..."
                />
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!formData.tenantId}>
                Assign License
              </button>
            </div>
          </div>
        </div>
      )}

      {editingLicense && (
        <div className="platform-modal-overlay" onClick={() => setEditingLicense(null)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><Edit3 size={18} /> Edit License - {editingLicense.tenant?.name}</h3>
              <button onClick={() => setEditingLicense(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="form-group">
                <label>Plan</label>
                <select
                  className="form-select"
                  value={formData.sku}
                  onChange={e => handleSkuChange(e.target.value)}
                >
                  {SKU_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="SUSPENDED">Suspended</option>
                  <option value="REVOKED">Revoked</option>
                </select>
              </div>
              <div className="md-lic-form-row">
                <div className="form-group">
                  <label>Max Endpoints</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxEndpoints}
                    onChange={e => setFormData(prev => ({ ...prev, maxEndpoints: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Users</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxUsers}
                    onChange={e => setFormData(prev => ({ ...prev, maxUsers: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Integrations</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.maxIntegrations}
                    onChange={e => setFormData(prev => ({ ...prev, maxIntegrations: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-input"
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingLicense(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="platform-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="platform-modal delete" onClick={e => e.stopPropagation()}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} className="icon-danger" /> Revoke License</h3>
              <button onClick={() => setDeleteTarget(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <p className="platform-delete-message">
                This will revoke the CCM license for this tenant. They will lose access to all CCM features.
              </p>
              <div className="platform-delete-item">{deleteTarget.tenant?.name}</div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleRevoke}>Revoke License</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CcmLicensesTab;
